import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLoggedInAs, usePostHog } from "shared/hooks";
import {
  NoteCreateUpdateProps,
  NotesContext,
  LinkedToEntity,
} from "./NotesContext";
import {
  EnumEntityReferenceType,
  EnumOwnerType,
  Multilingual,
  NoteManyDocument,
  NoteManyQuery,
  OperatorManyUseCaseEnum,
  SortFindManyNoteInput,
  useListManyNamesLazyQuery,
  useNoteCreateOneMutation,
  useNoteManyQuery,
  useNoteRemoveByIdMutation,
  useNoteUpdateByIdMutation,
  useOperatorManyNamesLazyQuery,
  usePeopleManyNamesLazyQuery,
  useUserManyForMentionsLazyQuery,
  useWidgetManyNamesLazyQuery,
} from "gql/generated";
import { useAppContext } from "useAppContext";
import { useTranslation } from "react-i18next";
import { useToastContext } from "@ttc3k/trekker";
import _ from "lodash";
import { createMultilingual } from "shared/utils";
import { NetworkStatus } from "@apollo/client";

// When tied to an entity, we'll load all notes in one go
const NOTES_PER_FETCH_WITH_ENTITY = 10000;
// When not tied to an entity, we'll load 100 notes at a time
const NOTES_PER_FETCH = 100;

type NotesContextProviderProps = {
  waitForDefaultEntity?: boolean;
  defaultEntityId?: string;
  defaultEntityType?: EnumEntityReferenceType;
  defaultEntityName?: Multilingual;
  allowArchiving?: boolean;
  allowDeleting?: boolean;
  hideDisplayDate?: boolean; // This should be true when displayed in date groups
  includeArchivedNotes?: boolean;
  viewOnly?: boolean;
};

export const NotesContextProvider = ({
  waitForDefaultEntity = false,
  defaultEntityId,
  defaultEntityType,
  allowArchiving = true,
  allowDeleting = true,
  hideDisplayDate = false,
  includeArchivedNotes = false,
  defaultEntityName,
  viewOnly = false,
  children,
}: PropsWithChildren<NotesContextProviderProps>) => {
  const { dbUser } = useAppContext();
  const loggedInAs = useLoggedInAs();
  const { t } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  // Determine the fetch limit based on the entity type
  const fetchLimit = useMemo(
    () => (defaultEntityId ? NOTES_PER_FETCH_WITH_ENTITY : NOTES_PER_FETCH),
    [defaultEntityId],
  );

  /**
   * Display a success toast
   * @param type - The type of success
   * @returns void
   */
  const displaySuccessToast = useCallback(
    (type: "SAVED" | "ARCHIVED" | "UNARCHIVED" | "DELETED") =>
      toastFactory.create({
        title: t("SUCCESS"),
        description: t(`NOTES.SUCCESS.${type}`),
      }),
    [toastFactory, t],
  );

  /**
   * Display an error toast
   * @param type - The type of error
   * @returns void
   */
  const displayErrorToast = useCallback(
    (
      error: Error,
      type: "CREATING" | "UPDATING" | "ARCHIVING" | "UNARCHIVING" | "DELETING",
    ) =>
      toastFactory.create({
        title: t("ERROR.TITLE"),
        description:
          error instanceof Error ? error.message : t(`NOTES.ERROR.${type}`),
      }),
    [toastFactory, t],
  );

  // Fetch the users that can be mentioned in notes
  const [loadMentionableUsers, { data: mentionableUsersData }] =
    useUserManyForMentionsLazyQuery();
  useEffect(() => {
    if (!loggedInAs) return;

    loadMentionableUsers();
  }, [loggedInAs, loadMentionableUsers]);

  const [linkedToEntities, setLinkedToEntities] = useState<LinkedToEntity[]>(
    [],
  );
  const [loadingLinkedToEntities, setLoadingLinkedToEntities] = useState(false);

  const [loadOperators] = useOperatorManyNamesLazyQuery();
  const [loadWidgets] = useWidgetManyNamesLazyQuery();
  const [loadLists] = useListManyNamesLazyQuery();
  const [loadPeople] = usePeopleManyNamesLazyQuery();

  useEffect(() => {
    if (!loggedInAs) return;

    const loadEntities = async () => {
      setLoadingLinkedToEntities(true);
      const { data: operatorsData } = await loadOperators({
        variables: {
          filter: {
            organizations: [loggedInAs!.entityId],
          },
          useCase: OperatorManyUseCaseEnum.Internal,
          limit: 10000,
        },
      });
      const { data: widgetsData } = await loadWidgets({
        variables: {
          filter: {
            owner: {
              entityId: loggedInAs!.entityId,
              type: loggedInAs!.type,
            },
            archived: false,
          },
          limit: 10000,
        },
      });
      const { data: listsData } = await loadLists({
        variables: {
          filter: {
            owner: {
              entityId: loggedInAs!.entityId,
              type: loggedInAs!.type,
            },
            archived: false,
          },
          limit: 10000,
        },
      });
      const { data: peopleData } = await loadPeople({
        variables: {
          filter: {
            visibleTo: [
              {
                entity: {
                  entityId: loggedInAs?.entityId,
                  type: loggedInAs?.type,
                },
              },
            ],
          },
          limit: 10000,
        },
      });

      setLinkedToEntities(prev =>
        _.uniqBy(
          [
            ...prev,
            ...(listsData?.listMany ?? []).map(list => ({
              entityId: list._id,
              entityType: EnumEntityReferenceType.List,
              name: list.name,
            })),
            ...(widgetsData?.widgetManySimple ?? []).map(widget => ({
              entityId: widget._id,
              entityType: EnumEntityReferenceType.Widget,
              name: widget.name,
            })),
            ...(operatorsData?.operatorMany ?? []).map(operator => ({
              entityId: operator._entityId,
              entityType: EnumEntityReferenceType.Operator,
              name: operator.name,
            })),
            ...(peopleData?.peopleManySimple ?? []).map(person => ({
              entityId: person._id,
              entityType: EnumEntityReferenceType.Person,
              name: createMultilingual(
                `${person.firstName} ${person.lastName}`,
              ),
            })),
          ],
          "entityId",
        ),
      );
      setLoadingLinkedToEntities(false);
    };

    // If the user is logged in as an organization, we need to load the
    // operators, widgets, and lists that they have access to
    if (loggedInAs.type === EnumOwnerType.Organization) {
      loadEntities();
    }
  }, [loadLists, loadOperators, loadPeople, loadWidgets, loggedInAs]);

  const [initialLoad, setInitialLoad] = useState(true);
  const [notes, setNotes] = useState<NoteManyQuery["noteMany"]>([]);
  const [hasMore, setHasMore] = useState(false);

  /**
   * Updates the notes state
   * - If resetState is true, we will reset the notes state
   * - Otherwise, we will update the notes state
   * - We will also set the hasMore state
   * @param newNotes - The new notes
   * @param resetState - Whether to reset the notes state
   * @returns void
   */
  const updateNotesState = useCallback(
    (newNotes: NoteManyQuery["noteMany"], resetState: boolean = false) => {
      const updatedNotes = resetState
        ? newNotes
        : _.uniqBy([...notes, ...newNotes], "_id");
      const hasMore = updatedNotes.length === fetchLimit;

      setNotes(updatedNotes);
      setHasMore(hasMore);
    },
    [fetchLimit, notes],
  );

  const { loading, error, fetchMore, networkStatus } = useNoteManyQuery({
    variables: {
      filter: {
        // If there is no default entityId/entityType, we'll load all notes for the owner
        ...(defaultEntityId && defaultEntityType
          ? {
              entity: {
                _entityId: defaultEntityId,
                type: defaultEntityType,
              },
            }
          : undefined),
        ...(!includeArchivedNotes && {
          isArchived: false,
        }),
        // In Adminland, we want ALL notes for the entity
        ...(loggedInAs?.type !== EnumOwnerType.TnbInternal
          ? {
              owner: { entityId: loggedInAs?.entityId, type: loggedInAs?.type },
            }
          : undefined),
      },
      sort: SortFindManyNoteInput.UpdatedDesc,
      limit: fetchLimit,
      skip: 0,
    },
    skip: !loggedInAs || (waitForDefaultEntity && !defaultEntityId),
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      updateNotesState(data.noteMany, networkStatus === NetworkStatus.refetch);
      setInitialLoad(false);
    },
  });

  const fetchMoreNotes = useCallback(async () => {
    const { data } = await fetchMore({
      variables: {
        skip: notes.length,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        if (!fetchMoreResult?.noteMany?.length) {
          return { noteMany: [] };
        }

        // Since we're using no-cache, we need to manually merge the results
        return {
          noteMany: fetchMoreResult.noteMany,
        };
      },
    });

    updateNotesState(data?.noteMany ?? [], false);
  }, [fetchMore, notes.length, updateNotesState]);

  useEffect(() => {
    /**
     * Fetch more notes when the hasMore state is true
     */
    if (!fetchMore || !hasMore) return;
    fetchMoreNotes();
  }, [fetchMore, fetchMoreNotes, hasMore]);

  const [createNoteMutation, { loading: isCreatingNote }] =
    useNoteCreateOneMutation({
      refetchQueries: [NoteManyDocument],
      awaitRefetchQueries: true,
    });

  /**
   * Creates a new note
   * - Adds the new note to the allNotes ref and the notes state
   */
  const createNote = useCallback(
    async (note: NoteCreateUpdateProps) => {
      if (!dbUser || !loggedInAs) return false;

      const entityReference = note.entity ?? {
        _entityId: defaultEntityId as string,
        type: defaultEntityType as EnumEntityReferenceType,
      };
      try {
        const { data, errors } = await createNoteMutation({
          variables: {
            record: {
              mentions: note.mentions ?? [],
              message: note.message,
              ...(note.noteDateISO && {
                noteDateISO: note.noteDateISO,
              }),
              entity: entityReference,
              author: dbUser._id,
              owner: loggedInAs,
            },
          },
        });
        if (errors) {
          throw new Error(errors[0].message);
        }
        if (data?.noteCreateOne?.record) {
          displaySuccessToast("SAVED");
          return true;
        }
      } catch (error) {
        trackPostHogException(error, {
          fileName: "NotesContextProvider.tsx",
          method: "createNote",
          message: "Error creating note",
        });
        displayErrorToast(error as Error, "CREATING");
      }
      return false;
    },
    [
      defaultEntityId,
      defaultEntityType,
      dbUser,
      loggedInAs,
      createNoteMutation,
      displaySuccessToast,
      displayErrorToast,
      trackPostHogException,
    ],
  );

  const [updateNoteMutation, { loading: isUpdatingNote }] =
    useNoteUpdateByIdMutation({
      refetchQueries: [NoteManyDocument],
      awaitRefetchQueries: true,
    });
  /**
   * Updates a note
   * - Updates the note in the allNotes ref and the notes state
   */
  const updateNote = useCallback(
    async (noteId: string, note: NoteCreateUpdateProps) => {
      if (!dbUser || !loggedInAs) return false;

      const entityReference = note.entity;

      try {
        const { data, errors } = await updateNoteMutation({
          variables: {
            id: noteId,
            record: {
              mentions: note.mentions ?? [],
              message: note.message,
              ...(note.noteDateISO && {
                noteDateISO: note.noteDateISO,
              }),
              entity: entityReference,
            },
          },
        });
        if (errors) {
          throw new Error(errors[0].message);
        }
        if (data?.noteUpdateById?.record) {
          displaySuccessToast("SAVED");
          return true;
        }
      } catch (error) {
        trackPostHogException(error, {
          fileName: "NotesContextProvider.tsx",
          method: "updateNote",
          message: "Error updating note",
        });
        displayErrorToast(error as Error, "UPDATING");
      }
      return false;
    },
    [
      dbUser,
      loggedInAs,
      updateNoteMutation,
      displaySuccessToast,
      displayErrorToast,
      trackPostHogException,
    ],
  );

  /**
   * Archives a note
   * - If includeArchivedNotes is false, we will remove it from the allNotes ref
   */
  const archiveNote = useCallback(
    async (noteId: string, isArchived: boolean) => {
      if (!dbUser || !loggedInAs) return false;

      try {
        const { data, errors } = await updateNoteMutation({
          variables: {
            id: noteId,
            record: {
              isArchived,
            },
          },
        });
        if (errors) {
          throw new Error(errors[0].message);
        }
        if (data?.noteUpdateById?.record) {
          displaySuccessToast("ARCHIVED");
          return true;
        }
      } catch (error) {
        trackPostHogException(error, {
          fileName: "NotesContextProvider.tsx",
          method: "archiveNote",
          message: "Error archiving note",
        });
        displayErrorToast(
          error as Error,
          isArchived ? "ARCHIVING" : "UNARCHIVING",
        );
      }
      return false;
    },
    [
      dbUser,
      loggedInAs,
      updateNoteMutation,
      displaySuccessToast,
      displayErrorToast,
      trackPostHogException,
    ],
  );

  const [deleteNoteMutation, { loading: isDeletingNote }] =
    useNoteRemoveByIdMutation({
      refetchQueries: [NoteManyDocument],
      awaitRefetchQueries: true,
    });
  /**
   * Delete a note
   */
  const deleteNote = useCallback(
    async (noteId: string) => {
      if (!dbUser || !loggedInAs) return false;

      try {
        const { data, errors } = await deleteNoteMutation({
          variables: {
            id: noteId,
          },
        });
        if (errors) {
          throw new Error(errors[0].message);
        }
        if (data?.noteRemoveById?.record) {
          displaySuccessToast("DELETED");
          return true;
        }
      } catch (error) {
        trackPostHogException(error, {
          fileName: "NotesContextProvider.tsx",
          method: "deleteNote",
          message: "Error deleting note",
        });
        displayErrorToast(error as Error, "DELETING");
      }
      return false;
    },
    [
      dbUser,
      loggedInAs,
      deleteNoteMutation,
      displaySuccessToast,
      displayErrorToast,
      trackPostHogException,
    ],
  );

  /**
   * The value of the context provider
   */
  const value = useMemo(
    () => ({
      defaultNoteEntity:
        defaultEntityId && defaultEntityType
          ? {
              _entityId: defaultEntityId,
              type: defaultEntityType,
            }
          : null,
      defaultNoteEntityName: defaultEntityName ?? null,
      allowArchiving,
      allowDeleting,
      hideDisplayDate,
      notes,
      loading,
      error,
      hasMore,
      initialLoad,
      isSavingNote: isCreatingNote || isUpdatingNote || isDeletingNote,
      mentionableUsers: _.orderBy(
        mentionableUsersData?.userManyForMentions?.map(user => ({
          id: user._id,
          display: `${user.firstName} ${user.lastName}`,
        })) ?? [],
        "display",
        "asc",
      ),
      createNote,
      updateNote,
      archiveNote,
      deleteNote,
      linkedToEntities,
      loadingLinkedToEntities,
      viewOnly,
    }),
    [
      defaultEntityId,
      defaultEntityType,
      defaultEntityName,
      allowArchiving,
      allowDeleting,
      hideDisplayDate,
      notes,
      loading,
      error,
      hasMore,
      initialLoad,
      isCreatingNote,
      isUpdatingNote,
      isDeletingNote,
      mentionableUsersData?.userManyForMentions,
      createNote,
      updateNote,
      archiveNote,
      deleteNote,
      linkedToEntities,
      loadingLinkedToEntities,
      viewOnly,
    ],
  );

  return (
    <NotesContext.Provider value={value}>{children}</NotesContext.Provider>
  );
};
